import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import styles from '../styles.module.css';
import { CompanyForm } from './companyForm';
import {
  Company,
  companiesFormDataType,
  handleChangeCompaniesDataType
} from './types';
import { createCompanySchema } from './utils';
import { companiesFormData } from '../../../constants/companies';
import CompanyIcon from '@/assets/icons/union.svg?react';
import {
  AdminCardButtons,
  AdminCardHeader
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import { Button } from '@/components/Form/Button';
import { getZodSchemaErrors } from '@/components/Form/Input/utils';
import { adminToolPaths } from '@/constants/adminTool';
import { useModal } from '@/context/ModalContext';
import { useToast } from '@/context/ToastContext';
import { useCreateCompany } from '@/hooks/services/Companies';

function NewCompany() {
  const navigate = useNavigate();
  const { showTypedModal } = useModal();
  const { showToast } = useToast();
  const [initialLoading, setInitialLoading] = useState(true);
  const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
  const [onSubmit, setOnSubmit] = useState(false);

  setTimeout(() => {
    setInitialLoading(false);
  }, 1000);

  const { mutate: createCompany, isPending, error, reset } = useCreateCompany();

  const [formData, setFormData] =
    useState<companiesFormDataType>(companiesFormData);

  const setParsedZodData = (formData: any) => {
    const parsed = createCompanySchema.safeParse({
      ...formData
    });

    if (!parsed.success) {
      const errs = getZodSchemaErrors(parsed);
      setFormErrors(errs);
    } else {
      setFormErrors({});
    }

    return parsed;
  };

  const handleChangeFormData: handleChangeCompaniesDataType = (
    field,
    value
  ) => {
    const newFormData = {
      ...formData,
      [field]: value
    };

    if (onSubmit) {
      setParsedZodData(newFormData);
    }
    setFormData(newFormData);
  };

  const onCreate = () => {
    setOnSubmit(true);
    const parsed = setParsedZodData(formData);

    if (parsed.success) {
      setFormErrors({});
      createCompany(formData as Company, {
        onSuccess: () => {
          showTypedModal({
            type: 'success',
            title: 'Company was successfully created'
          });
          setTimeout(() => {
            navigate(`${adminToolPaths.companies.root}/${formData.name}`, {
              state: { companyName: formData.name }
            });
          }, 3000);
        },
        onError: error => {
          console.error('Error on creating company:', error);
        }
      });
    }
  };

  const onCancel = () => {
    setOnSubmit(false);
    setFormErrors({});
    setFormData(companiesFormData as companiesFormDataType);
    navigate(adminToolPaths.companies.root);
  };

  useEffect(() => {
    if (error) {
      showToast(error.message || 'An error occurred while creating company', {
        type: 'error',
        autoClose: 5000,
        actionText: 'Try again',
        onActionClick: () => {
          reset();
          onCreate();
        }
      });
    }
  }, [error]);

  if (initialLoading) {
    return <ContainerLoader />;
  }

  return (
    <div className={styles.adminWrapper}>
      <div className={styles.adminContainer}>
        <AdminCardHeader
          left={
            <>
              <CompanyIcon className={styles.adminIconHeader} />
              <h2 className={styles.adminTitle}>New Company</h2>
            </>
          }
        />
        <CompanyForm
          formData={formData}
          handleChangeFormData={handleChangeFormData}
          formErrors={formErrors}
          container="create"
        />
      </div>

      <AdminCardButtons>
        <Button
          onClick={() => onCancel()}
          color="blue"
          solid={false}
          label="CANCEL"
          size="large"
        />
        <Button
          onClick={() => onCreate()}
          color="blue"
          label="SAVE"
          size="large"
          loading={isPending}
        />
      </AdminCardButtons>
    </div>
  );
}

export default NewCompany;
